import { Alert, Container } from "@firecms/ui";
import { PromoCodeCreateSchema, type PromoCodeList } from "@repo/common";
import { useState } from "react";
import { ZodError } from "zod";

import PromoCodesList from "components/organisms/PromoCodesList";

import { defaultFormValues } from "./PromoCodesCreate.constants";
import { usePromoCodesCreate } from "./hooks/usePromoCodesCreate";
import { mapFormSchemaToServerSchema } from "./PromoCodesCreate.mappers";
import {
  type PromoCodesCreateFormFieldErrors,
  PromoCodesCreateFormSchema,
  type PromoCodesCreateFormType,
} from "./PromoCodesCreate.types";
import { mapZodErrors } from "./PromoCodesCreate.utils";
import PromoCodesCreateForm from "./ui/PromoCodesCreateForm";

const PromoCodesCreate: React.FC = () => {
  const { createPromoCodes, loading, error, success, reset } = usePromoCodesCreate();
  const [formValues, setFormValues] = useState<PromoCodesCreateFormType>(defaultFormValues);
  const [fieldErrors, setFieldErrors] = useState<PromoCodesCreateFormFieldErrors>({});
  const [rootError, setRootError] = useState<string | null>(null);
  const [createdCodes, setCreatedCodes] = useState<PromoCodeList | null>(null);

  const updateField = <K extends keyof PromoCodesCreateFormType>(
    field: K,
    value: PromoCodesCreateFormType[K],
  ): void => {
    setFormValues((previous) => ({ ...previous, [field]: value }));
    setFieldErrors((previous) => ({ ...previous, [field]: undefined }));
    setRootError(null);
  };

  const handleSubmit = async (): Promise<void> => {
    setFieldErrors({});
    setRootError(null);
    reset();

    const parsedForm = PromoCodesCreateFormSchema.safeParse(formValues);

    if (!parsedForm.success) {
      setFieldErrors(mapZodErrors(parsedForm.error));
      return;
    }

    const mappedData = mapFormSchemaToServerSchema(parsedForm.data);

    try {
      const parsedServerData = PromoCodeCreateSchema.parse(mappedData);
      const response = await createPromoCodes(parsedServerData);
      setCreatedCodes(response);
    } catch (submitError) {
      if (submitError instanceof ZodError) {
        setRootError(submitError.issues[0]?.message ?? "Validierungsfehler");
      }
    }
  };

  if (success && createdCodes) {
    return (
      <Container className="max-w-4xl py-8">
        <Alert color="success">Die Promo Codes wurden erfolgreich erstellt.</Alert>
        <PromoCodesList preLoadedCodes={createdCodes} />
      </Container>
    );
  }

  return (
    <Container className="flex max-w-2xl flex-col gap-4 py-8">
      {error ? <Alert color="error">Fehler beim Erstellen der Promo Codes: {error}</Alert> : null}

      <PromoCodesCreateForm
        formValues={formValues}
        fieldErrors={fieldErrors}
        rootError={rootError}
        loading={loading}
        onFieldChange={updateField}
        onSubmit={handleSubmit}
      />
    </Container>
  );
};

export default PromoCodesCreate;
